import React, { useState, useEffect, useCallback } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import config from "../../Config";
import TransportMenu from "./TransportMenu";
import Spinner from "react-bootstrap/Spinner";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import { scrollToTop } from "../Shared/ScrollOnTop";

/*
 * Vehicle profit and loss - what each bus earns against what it costs to run.
 *
 * Revenue is read from the fee ledger (fee code 005), so Billed and Received are two
 * different numbers. The system this replaces reported one "revenue" figure taken from a
 * snapshot of currently registered members, which could not distinguish an invoice from a
 * payment - a bus whose parents had not paid looked exactly as profitable as one whose
 * had. Here the unpaid amount is a column of its own.
 */
function VehiclePLReport() {
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const UserName = localStorage.getItem("UserName");
  const MemberType = localStorage.getItem("MemberType");

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [vehicles, setVehicles] = useState([]);

  const thisYear = moment().year();
  const [fromMonth, setFromMonth] = useState(moment().startOf("year").format("YYYY-MM"));
  const [toMonth, setToMonth] = useState(moment().format("YYYY-MM"));
  const [vehicleId, setVehicleId] = useState("");
  const [groupByBus, setGroupByBus] = useState(true);

  const auth = useCallback(
    (extra) => ({
      AccessKey,
      UserID,
      UserName,
      MemberType: MemberType || "Admin",
      SchoolID: 1,
      ...extra,
    }),
    [AccessKey, UserID, UserName, MemberType]
  );

  const post = useCallback(
    (url, data) =>
      axios({
        method: "post",
        url: config.base_url + url,
        headers: { "Content-Type": "application/json" },
        data,
      }),
    []
  );

  const load = useCallback(() => {
    setLoading(true);
    const f = moment(fromMonth, "YYYY-MM");
    const t = moment(toMonth, "YYYY-MM");

    post(
      "TransportReport/GetVehiclePL",
      auth({
        Vehicle_ID: vehicleId ? parseInt(vehicleId, 10) : 0,
        From_Year: f.isValid() ? f.year() : thisYear,
        From_Month: f.isValid() ? f.month() + 1 : 1,
        To_Year: t.isValid() ? t.year() : thisYear,
        To_Month: t.isValid() ? t.month() + 1 : 12,
      })
    )
      .then((res) => {
        if (res.data.status_code === 1) {
          setRows(res.data.ListofRecords || []);
          setSummary(res.data.Summary || null);
        } else {
          setRows([]);
          setSummary(null);
          toast.error(res.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(() =>
        toast.error("Network error", { position: toast.POSITION.TOP_RIGHT })
      )
      .finally(() => setLoading(false));
  }, [post, auth, vehicleId, fromMonth, toMonth, thisYear]);

  useEffect(() => {
    document.title = "Vehicle Profit & Loss";
    scrollToTop();
    post("Vehicle/GetVehicles", auth({}))
      .then((res) => {
        if (res.data.status_code === 1) setVehicles(res.data.ListofRecords || []);
      })
      .catch(() => setVehicles([]));
  }, [post, auth]);

  useEffect(() => {
    load();
  }, [load]);

  const money = (n) =>
    n === null || n === undefined
      ? "—"
      : Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });

  /* One row per bus, months summed - the shape most people want first. */
  const byBus = () => {
    const map = new Map();
    rows.forEach((r) => {
      const k = r.Vehicle_ID;
      if (!map.has(k)) {
        map.set(k, {
          Vehicle_ID: r.Vehicle_ID,
          Registration_No: r.Registration_No,
          Model: r.Model,
          Months: 0,
          Students: 0,
          Billed: 0,
          Received: 0,
          Outstanding: 0,
          FuelLitres: 0,
          FuelCost: 0,
          MaintCost: 0,
          PartsCost: 0,
          TotalCost: 0,
          DistanceKM: 0,
        });
      }
      const a = map.get(k);
      a.Months += 1;
      // Students is a headcount per month, so the peak is meaningful where a sum is not.
      a.Students = Math.max(a.Students, r.Students);
      a.Billed += r.Billed;
      a.Received += r.Received;
      a.Outstanding += r.Outstanding;
      a.FuelLitres += r.FuelLitres;
      a.FuelCost += r.FuelCost;
      a.MaintCost += r.MaintCost;
      a.PartsCost += r.PartsCost;
      a.TotalCost += r.TotalCost;
      a.DistanceKM += r.DistanceKM;
    });
    return Array.from(map.values()).map((a) => ({
      ...a,
      Profit: a.Received - a.TotalCost,
      KmPerLitre: a.DistanceKM > 0 && a.FuelLitres > 0 ? a.DistanceKM / a.FuelLitres : null,
    }));
  };

  const display = groupByBus ? byBus() : rows;

  const exportCsv = () => {
    const head = groupByBus
      ? ["Bus", "Model", "Months", "Peak students", "Billed", "Received", "Outstanding", "Fuel", "Maintenance", "Parts", "Total cost", "Profit"]
      : ["Bus", "Year", "Month", "Students", "Billed", "Received", "Outstanding", "Fuel", "Maintenance", "Parts", "Total cost", "Profit"];

    const body = display.map((r) =>
      groupByBus
        ? [r.Registration_No, r.Model || "", r.Months, r.Students, r.Billed, r.Received, r.Outstanding, r.FuelCost, r.MaintCost, r.PartsCost, r.TotalCost, r.Profit]
        : [r.Registration_No, r.Yr, r.MonthName, r.Students, r.Billed, r.Received, r.Outstanding, r.FuelCost, r.MaintCost, r.PartsCost, r.TotalCost, r.ProfitOnReceived]
    );

    const csv = [head, ...body]
      .map((line) => line.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    link.download = `VehiclePL_${fromMonth}_to_${toMonth}.csv`;
    link.click();
  };

  const printUrl = () =>
    `/Transport/PrintVehiclePL?from=${fromMonth}&to=${toMonth}&vehicle=${vehicleId || ""}&group=${groupByBus ? 1 : 0}`;

  const renderRows = () => {
    if (display.length === 0) {
      return (
        <tr>
          <td colSpan={groupByBus ? 12 : 12} className="text-center text-muted py-4">
            Nothing posted in this period
          </td>
        </tr>
      );
    }

    return display.map((r, i) => {
      const profit = groupByBus ? r.Profit : r.ProfitOnReceived;
      return (
        <tr key={groupByBus ? r.Vehicle_ID : `${r.Vehicle_ID}-${r.Yr}-${r.Mn}`}>
          <td>{i + 1}</td>
          <td className="fw-bold">{r.Registration_No}</td>
          <td>{groupByBus ? r.Model || "—" : `${r.MonthName} ${r.Yr}`}</td>
          <td className="text-end">{r.Students}</td>
          <td className="text-end">{money(r.Billed)}</td>
          <td className="text-end">{money(r.Received)}</td>
          <td className="text-end">
            {r.Outstanding > 0 ? (
              <span className="text-danger">{money(r.Outstanding)}</span>
            ) : (
              "—"
            )}
          </td>
          <td className="text-end">{money(r.FuelCost)}</td>
          <td className="text-end">{r.MaintCost > 0 ? money(r.MaintCost) : "—"}</td>
          <td className="text-end">{r.PartsCost > 0 ? money(r.PartsCost) : "—"}</td>
          <td className="text-end">{money(r.TotalCost)}</td>
          <td className="text-end fw-bold">
            <span className={profit >= 0 ? "text-success" : "text-danger"}>
              {money(profit)}
            </span>
          </td>
        </tr>
      );
    });
  };

  const totalOf = (field) => display.reduce((s, r) => s + (r[field] || 0), 0);

  return (
    <>
      <TransportMenu />
      <ToastContainer />

      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-7">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <NavLink to="/Dashboard">Dashboard</NavLink>
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/Transport/TransportDashboard">Transport</NavLink>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Vehicle P&amp;L
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Vehicle Profit &amp; Loss</div>
                <p className="text-muted small mb-0">
                  Transport fee billed and received against each bus, less what it cost to
                  run. Unpaid fee is shown separately — it is not income until it arrives.
                </p>
              </div>
              <div className="col-md-5">
                <div className="page-right-actions">
                  <button
                    type="button"
                    onClick={exportCsv}
                    className="button button-white button-icon"
                  >
                    Export
                    <i className="ri-file-excel-line"></i>
                  </button>
                  <a
                    href={printUrl()}
                    target="_blank"
                    rel="noreferrer"
                    className="button button-white button-icon"
                  >
                    Print
                    <i className="ri-printer-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="container">
            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Fleet revenue (received)</div>
                    <div className="h4 mb-0">
                      Rs. {summary ? money(summary.FleetRevenue) : 0}
                    </div>
                    <div className="small text-muted">
                      of {summary ? money(summary.FleetBilled) : 0} billed
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Fleet expense</div>
                    <div className="h4 mb-0">
                      Rs. {summary ? money(summary.FleetExpense) : 0}
                    </div>
                    <div className="small text-muted">
                      fuel {summary ? money(summary.FuelCost) : 0} · maint{" "}
                      {summary ? money(summary.MaintCost) : 0}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Fleet profit</div>
                    <div
                      className={`h4 mb-0 ${
                        summary && summary.FleetProfit < 0 ? "text-danger" : "text-success"
                      }`}
                    >
                      Rs. {summary ? money(summary.FleetProfit) : 0}
                    </div>
                    <div className="small text-muted">
                      {summary ? summary.Vehicles : 0} buses
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Unpaid transport fee</div>
                    <div className="h4 mb-0 text-danger">
                      Rs. {summary ? money(summary.FleetOutstanding) : 0}
                    </div>
                    <div className="small text-muted">not counted as revenue</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="row g-2 mb-3 align-items-end">
                  <div className="col-md-2">
                    <label className="form-label small mb-1">From month</label>
                    <input
                      type="month"
                      className="form-control"
                      value={fromMonth}
                      onChange={(e) => setFromMonth(e.target.value)}
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small mb-1">To month</label>
                    <input
                      type="month"
                      className="form-control"
                      value={toMonth}
                      onChange={(e) => setToMonth(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small mb-1">Vehicle</label>
                    <select
                      className="form-select"
                      value={vehicleId}
                      onChange={(e) => setVehicleId(e.target.value)}
                    >
                      <option value="">All vehicles</option>
                      {vehicles.map((v) => (
                        <option key={v.Vehicle_ID} value={v.Vehicle_ID}>
                          {v.Registration_No}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <div className="form-check mt-4">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="groupByBus"
                        checked={groupByBus}
                        onChange={(e) => setGroupByBus(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="groupByBus">
                        One row per bus
                      </label>
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-theme">
                    <thead>
                      <tr>
                        <th>Sr.</th>
                        <th>Bus</th>
                        <th>{groupByBus ? "Model" : "Month"}</th>
                        <th className="text-end">
                          {groupByBus ? "Peak students" : "Students"}
                        </th>
                        <th className="text-end">Billed</th>
                        <th className="text-end">Received</th>
                        <th className="text-end">Unpaid</th>
                        <th className="text-end">Fuel</th>
                        <th className="text-end">Maint.</th>
                        <th className="text-end">Parts</th>
                        <th className="text-end">Total cost</th>
                        <th className="text-end">Profit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="12" className="text-center py-4">
                            <Spinner animation="border" role="status" />
                          </td>
                        </tr>
                      ) : (
                        renderRows()
                      )}
                    </tbody>
                    {!loading && display.length > 0 ? (
                      <tfoot>
                        <tr className="fw-bold">
                          <th colSpan={4} className="text-end">
                            Totals
                          </th>
                          <th className="text-end">{money(totalOf("Billed"))}</th>
                          <th className="text-end">{money(totalOf("Received"))}</th>
                          <th className="text-end">{money(totalOf("Outstanding"))}</th>
                          <th className="text-end">{money(totalOf("FuelCost"))}</th>
                          <th className="text-end">{money(totalOf("MaintCost"))}</th>
                          <th className="text-end">{money(totalOf("PartsCost"))}</th>
                          <th className="text-end">{money(totalOf("TotalCost"))}</th>
                          <th className="text-end">
                            {money(
                              totalOf(groupByBus ? "Profit" : "ProfitOnReceived")
                            )}
                          </th>
                        </tr>
                      </tfoot>
                    ) : null}
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default VehiclePLReport;
